import { useQuery } from '@tanstack/react-query'
import { api, type PageResult } from '@/lib/api'
import { endOfLocalDayUtc, startOfLocalDayUtc } from '@/lib/datetime'

/**
 * Audit Log — Super Admin only (spec Requirement 9). Append-only: there are no
 * create, update or delete endpoints, and none will be added.
 *
 * Actor and target are stored as text alongside their IDs because consumer
 * accounts hard-delete. An ID-only reference would point at nothing once the
 * account is gone — which is exactly when the history matters most.
 */

export interface AuditChange {
  from: unknown
  to: unknown
}

export interface AuditRow {
  id: number
  actor_id: string
  actor_name: string
  actor_email: string
  actor_role: string
  action: string
  target_type: string
  target_id: string
  target_desc: string
  /** Present on edits only; creates have nothing to diff against. */
  changes?: Record<string, AuditChange>
  ip_address?: string
  created_at: string
}

/** The actions the service writes, per the contract. */
export const AUDIT_ACTIONS = [
  'quest_created',
  'quest_updated',
  'task_verified',
  'task_rejected',
  'reward_granted',
  'reward_rejected',
  'promo_banner_updated',
  'sponsored_coin_created',
  'sponsored_coin_updated',
  'sponsored_coin_deleted',
  'app_patch_policy_updated',
  'app_patch_mandatory_rule_set',
  'app_patch_mandatory_rule_cleared',
  'app_patch_mandatory_rule_lowered',
  'app_native_version_policy_updated',
  'shorebird_patch_promoted',
  'shorebird_patch_rolled_back',
  'shorebird_patch_rolled_forward',
  'trending_created',
  'trending_updated',
  'trending_published',
  'trending_deleted',
  'role_granted',
  'role_changed',
  'role_revoked',
  'i18n_message_created',
  'i18n_message_updated',
  'i18n_message_renamed',
  'i18n_baseline_updated',
  'i18n_translation_overridden',
  'i18n_language_created',
  'i18n_language_updated',
  'i18n_language_enabled',
  'i18n_language_disabled',
  'i18n_module_created',
  'i18n_module_updated',
  'i18n_module_disabled',
  'i18n_translation_replaced',
  'i18n_published',
  'i18n_rolled_back',
] as const

export const ACTION_LABEL: Record<string, string> = {
  quest_created: 'Quest created',
  quest_updated: 'Quest updated',
  task_verified: 'Task verified',
  task_rejected: 'Task rejected',
  reward_granted: 'Reward granted',
  reward_rejected: 'Reward rejected',
  promo_banner_updated: 'Promo banner updated',
  sponsored_coin_created: 'Sponsored coin added',
  sponsored_coin_updated: 'Sponsored coin updated',
  sponsored_coin_deleted: 'Sponsored coin deleted',
  app_patch_policy_updated: 'App patch policy updated',
  // The mandatory Shorebird patch floor — scoped per platform+release_version, unlike
  // app_patch_policy_updated above which only ever changes the un-scoped message row. Three
  // distinct actions so the log can tell a staff member setting/clearing one apart from the
  // system auto-lowering one after a rollback (service.LowerMandatoryFloorIfUnreachable).
  app_patch_mandatory_rule_set: 'Mandatory update floor set',
  app_patch_mandatory_rule_cleared: 'Mandatory update floor cleared',
  app_patch_mandatory_rule_lowered: 'Mandatory update floor auto-lowered after rollback',
  // A separate gate entirely — the native app-store-level forced update (minimum native
  // version+build), independent of the three Shorebird-patch actions above.
  app_native_version_policy_updated: 'Native version gate updated',
  // The actual release gate: a staff member moved one Shorebird patch from a
  // staging channel onto "stable" — the moment it became visible to
  // production users. Distinct from app_patch_policy_updated, which only sets
  // this console's own "mandatory patch" policy row.
  shorebird_patch_promoted: 'Shorebird patch promoted to stable',
  // The rollback counterpart — service.RollbackToPatch writes this action instead of
  // shorebird_patch_promoted so the two are distinguishable in the log. Not just a re-promote:
  // this marks every newer patch on the release as rolled back via Shorebird's own rollback
  // endpoint, since a channel tag alone doesn't stop a newer, still-tagged patch from winning.
  shorebird_patch_rolled_back: 'Shorebird patch rolled back',
  // The undo for the row above — service.RollforwardPatch reactivates a patch a rollback had
  // marked rolled back, via Shorebird's own rollforward endpoint.
  shorebird_patch_rolled_forward: 'Shorebird patch rolled forward',
  trending_created: 'Trending item created',
  trending_updated: 'Trending item updated',
  trending_published: 'Trending published',
  trending_deleted: 'Trending item deleted',
  role_granted: 'Role granted',
  role_changed: 'Role changed',
  role_revoked: 'Role revoked',
  i18n_message_created: 'Message key created',
  i18n_message_updated: 'Message key updated',
  i18n_message_renamed: 'Message key renamed',
  // Its own action rather than a field in "updated": this is the edit that
  // cascades, clearing machine drafts and flagging human ones for review.
  i18n_baseline_updated: 'English baseline changed',
  i18n_translation_overridden: 'Placeholder check overridden',
  i18n_language_created: 'Language added',
  i18n_language_updated: 'Language updated',
  i18n_language_enabled: 'Language enabled',
  i18n_language_disabled: 'Language disabled',
  i18n_module_created: 'Module added',
  i18n_module_updated: 'Module updated',
  i18n_module_disabled: 'Module disabled',
  // The three the backend has always written and this list never carried, so
  // they rendered as raw codes and could not be picked in the filter.
  //
  // The first one is why that mattered: it is the forced retranslate, the only
  // automated write that destroys a human translation, and the reason
  // app/i18n/service/audit.go records it at all is so it can be found later.
  i18n_translation_replaced: 'Human translation replaced',
  i18n_published: 'Language published',
  i18n_rolled_back: 'Language rolled back',
}

/** Green for something granted, red for something withheld or taken away. */
export function actionTone(action: string): 'success' | 'destructive' | 'accent' {
  if (
    action.endsWith('_rejected') ||
    action.endsWith('_revoked') ||
    action.endsWith('_deleted') ||
    // Disabling a language pulls it from every user; disabling a module stops
    // its keys being exported. Both are reversible, but they are what somebody
    // scanning this log for "what went wrong" is looking for.
    action.endsWith('_disabled')
  ) {
    return 'destructive'
  }
  if (action === 'reward_granted' || action === 'task_verified') return 'success'
  return 'accent'
}

export interface AuditQuery {
  page?: number
  pageSize?: number
  /**
   * `users.public_id`, matched against the row's **snapshotted** `actor_id` rather
   * than through a join — so it still finds the work of someone whose role was
   * revoked, or whose consumer account has since been hard-deleted.
   */
  actorId?: string
  action?: string
  /**
   * The operator's LOCAL calendar dates, `YYYY-MM-DD`, straight off
   * `<input type="date">`. Converted to UTC instants before they reach the
   * wire — see the queryFn. `to` is inclusive of its own day here; the
   * exclusive upper bound the API wants is derived, not sent by the caller.
   */
  from?: string
  to?: string
}

/** The query definition alone (no hook call), so a caller needing several at once (see
 * app-patch-policy-page.tsx) can pass these through `useQueries` instead of unrolling by hand. */
export function auditLogQueryOptions({ page = 1, pageSize = 20, actorId, action, from, to }: AuditQuery = {}) {
  return {
    queryKey: ['audit', page, pageSize, actorId ?? 'all', action ?? 'all', from ?? '', to ?? ''],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) })
      if (actorId) params.set('actor_id', actorId)
      if (action) params.set('action', action)
      // The API takes UTC instants on a half-open range; these are local
      // calendar dates. A malformed value yields null and is simply omitted,
      // which is the same as not filtering — the date input cannot produce one.
      const fromAt = from ? startOfLocalDayUtc(from) : null
      const toAt = to ? endOfLocalDayUtc(to) : null
      if (fromAt) params.set('from', fromAt)
      if (toAt) params.set('to', toAt)
      return api.get<PageResult<AuditRow>>(`/admin/audit?${params}`)
    },
  }
}

export function useAuditLog(query: AuditQuery = {}) {
  return useQuery(auditLogQueryOptions(query))
}
